'use client'

import {
  GoogleMapsPrediction,
  GoogleCoordinates,
  GoogleMapsStructuredPlace,
} from '../interfaces/googleMapsInterfaces'
import { useEffect, useState, useMemo, useRef } from 'react'
import CoincidenceOption from './CoincidenceOption'
import { getGoogleMapsPredictions } from '../services/googleMapsServices'
import { useAutocomplete } from '../hooks/useAutocomplete'
import { usePrevious } from "@wiggot/hooks"

let timeout: NodeJS.Timeout
let isTyping = false

interface Props {
  /**
   * This property indicates whether the CoincidencesList is visible.
   * @type {boolean}
   */
  isVisible: boolean

  /**
   * This is the searchText of the SearchField.
   * @type {string}
   */
  searchText: string

  /**
   * Indicates whether the street number must be validated.
   * @type {boolean}
   */
  validateStreetNumber?: boolean

  /**
   * Assigns default limits.
   * @type {google.maps.CircleLiteral}
   */
  defaultBounds?: google.maps.CircleLiteral

  /**
   * Assigns default country.
   * @type {string}
   */
  limitedCountry?: string

  /**
   * Assigns default regions.
   * @type {string[]}
   */
  limitedRegions?: string[]

  /**
   * Assign default limits, this overrides the "defaultBounds".
   * @type {{sw:GoogleCoordinate,ne:GoogleCoordinate}}
   */
  limitedBounds?: {
    sw: GoogleCoordinates
    ne: GoogleCoordinates
  }

  /**
   * This is the types of the AutocompleteResults
   * @type {string[]}
   * @default ["geocode"]
   */
  placeTypes?: string[]

  /**
   * This is the className of the Container
   * @type {string}
   * */
  itemClassName?: string

  itemTextClassName?: string

  withRemarkSelected?: boolean

  remarkSelectedClassNames?: string

  focusOptionClassNames?: string

  /**
   * This function is called when the match is selected.
   * @type {(place: GoogleMapsStructuredPlace) => void}
   */

  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * This function is called when the matches are loaded.
   * @type {(hasCoincidences: boolean) => void}
   * */
  onLoadedCoincidences: (hasCoincidences: boolean) => void
}

/**
 * This component is responsible for displaying the matches.
 * @param isVisible - This property indicates whether the CoincidencesList is visible.
 * @param searchText - This is the searchText of the SearchField.
 * @param validateStreetNumber - Indicates whether the street number must be validated.
 * @param defaultBounds - Assigns default limits.
 * @param limitedCountry - Assigns default country, default "MX".
 * @param limitedRegions - Assigns default regions.
 * @param limitedBounds - Assign default limits, this overrides the "defaultBounds".
 * @param placeTypes - This is the types of the AutocompleteResults
 * @param onSelected - This function is called when the match is selected.
 * @param onLoadedCoincidences - This function is called when the matches are loaded.
 * @param itemClassName - the classname for the item
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @returns {JSX.Element}
 * */
const CoincidencesList = (props: Props): JSX.Element => {
  const {
    isVisible,
    searchText,
    validateStreetNumber,
    defaultBounds,
    limitedCountry = 'MX',
    limitedRegions,
    limitedBounds,
    placeTypes = ['geocode'],
    itemClassName,
    withRemarkSelected,
    remarkSelectedClassNames,
    focusOptionClassNames,
    itemTextClassName,
    onSelected,
    onLoadedCoincidences,
  } = props

  const { selectPlace } = useAutocomplete()

  const [coincidences, setCoincidences] = useState<GoogleMapsPrediction[]>([])

  const [selectedIndex, setSelectedIndex] = useState(-1)

  const searchTextPrevious = usePrevious(searchText)

  const searchTextRef = useRef(searchText)
  searchTextRef.current = searchText

  const { hasStreetNumber, isVisibleNotCoincidences } = useMemo(() => {
    return {
      hasStreetNumber: /\d/.test(searchText),
      isVisibleNotCoincidences:
        !coincidences.length &&
        Boolean(searchText) &&
        searchTextPrevious !== '',
    }
  }, [coincidences, searchText])

  /**
   * This function is responsible for making the request to the Google API and obtaining the matches.
   * @param label - This is the label to search for.
   * @returns {Promise<void>}
   * */
  const findCoincidences = async (params: { label: string }): Promise<void> => {
    const { label } = params

    const request = preparePredictionsRequest({ label })

    const { isSuccess, data } = await getGoogleMapsPredictions(request)

    if (isTyping) {
      return
    }

    if (!isSuccess || !data?.length) {
      setCoincidences([])
      onLoadedCoincidences(false)
      return
    }

    let predictions: GoogleMapsPrediction[] = [...data]

    if (validateStreetNumber) {
      predictions = filterByStreetNumber({
        predictions,
      })
    }

    if (limitedRegions?.length) {
      predictions = filterByRegion({
        predictions,
      })
    }

    if (!predictions.length) {
      setCoincidences([])
      onLoadedCoincidences(false)
      return
    }

    setCoincidences([...predictions])
    onLoadedCoincidences(true)
  }

  /**
   * This function is responsible for filtering the matches by street number.
   * @param {GoogleMapsPrediction[]} predictions - This is the predictions to filter
   * @returns {GoogleMapsPrediction[]}
   * */
  const filterByStreetNumber = (params: {
    predictions: GoogleMapsPrediction[]
  }): GoogleMapsPrediction[] => {
    const { predictions } = params

    const filter = predictions.filter(({ description }) => {
      return (
        description &&
        description.match(/\d/g) !== null &&
        description.match(/\d/g)?.length !== 0
      )
    })

    return filter
  }

  /**
   * This function is responsible for filtering the matches by region.
   * @param predictions - This is the predictions to filter.
   * @returns GoogleMapsPrediction[]
   * */
  const filterByRegion = (params: {
    predictions: GoogleMapsPrediction[]
  }): GoogleMapsPrediction[] => {
    const { predictions } = params

    if (!limitedRegions?.length) {
      return predictions
    }

    const filter = predictions.filter(({ description }) => {
      const rgxRegion = new RegExp(limitedRegions.join('|'), 'i')
      const regionValidation = rgxRegion.test(description)
      return regionValidation
    })

    return filter
  }

  /**
   * This function is responsible for preparing the request to the Google API.
   * @param label - This is the label to search for.
   * @returns google.maps.places.AutocompletionRequest
   * */
  const preparePredictionsRequest = (params: {
    label: string
  }): google.maps.places.AutocompletionRequest => {
    const { label } = params

    let locationRestrictionBounds: any

    const request: {
      input: string
      locationRestriction?: any
      componentRestrictions?: any
      bounds?: any
      types: string[]
    } = {
      input: label,
      locationRestriction: null,
      componentRestrictions: {
        country: limitedCountry,
      },
      bounds: null,
      types: placeTypes,
    }
    
    if (window?.google || window?.google?.maps) {
      if (limitedBounds) {
        locationRestrictionBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(limitedBounds.sw.lat, limitedBounds.sw.lng), // SouthWest coord
          new google.maps.LatLng(limitedBounds.ne.lat, limitedBounds.ne.lng) //Northeast coord
        )
        request.locationRestriction = locationRestrictionBounds
  
        return request
      }
  
      if (defaultBounds) {
        const circle = new google.maps.Circle(defaultBounds)
        request.bounds = circle.getBounds()
      }
    }

    return request
  }

  /**
   * This function is responsible for handling the keydown event.
   * @param event - This is the event to handle.
   * @returns {Promise<void>}
   */
  const handleKeyDown = async (event: any): Promise<void> => {
    const totalOptions = coincidences.length

    if (!totalOptions || !isVisible) {
      setSelectedIndex(-1)
      return
    }

    if (event.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => (prevIndex + 1) % totalOptions)
    } else if (event.key === 'ArrowUp') {
      setSelectedIndex(
        (prevIndex) => (prevIndex - 1 + totalOptions) % totalOptions
      )
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      const select = await selectPlace({
        prediction: coincidences[selectedIndex],
      })

      if (!select) {
        return
      }

      onSelected(select)
      setSelectedIndex(-1)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [coincidences, selectedIndex])

  useEffect(() => {
    if (searchText.length < 3) {
      onLoadedCoincidences(false)
      setCoincidences([])
      return
    }

    isTyping = true

    clearTimeout(timeout)

    timeout = setTimeout(() => {
      isTyping = false
      if (searchText && searchTextRef.current === searchText) {
        findCoincidences({ label: searchText })
      }
    }, 200)

    if (!searchText) {
      onLoadedCoincidences(false)
      setCoincidences([])
    }
  }, [searchText])

  return (
    <>
      {isVisibleNotCoincidences ? (
        <p className="text-xs text-[#898CA8]">
          {validateStreetNumber && !hasStreetNumber
            ? 'Ingresa tu dirección completa para ver los resultados'
            : 'Sin sugerencias de búsqueda'}
        </p>
      ) : null}

      {coincidences.length ? (
        <div>
          <p className="text-xs text-[#898CA8]">Sugerencias</p>

          <div className="flex flex-col mt-2.5">
            {coincidences.map((coincidence, index) => {
              return (
                <CoincidenceOption
                  remarkSelectedClassNames={remarkSelectedClassNames}
                  focusOptionClassNames={focusOptionClassNames}
                  withRemarkSelected={withRemarkSelected}
                  searchText={searchText}
                  className={itemClassName}
                  itemTextClassName={itemTextClassName}
                  key={coincidence.placeId}
                  index={index}
                  selectedIndex={selectedIndex}
                  prediction={coincidence}
                  onSelected={onSelected}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default CoincidencesList
